const SOUND_ENABLED_STORAGE_KEY = 'dopamind_sound_enabled';

type SoundName = 'tap' | 'success' | 'warning' | 'toggle-on' | 'toggle-off' | 'achievement';

let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const BrowserAudioContext = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!BrowserAudioContext) {
    return null;
  }

  if (!audioContext) {
    audioContext = new BrowserAudioContext();
  }

  return audioContext;
};

export const isSoundEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  return window.localStorage.getItem(SOUND_ENABLED_STORAGE_KEY) !== 'false';
};

export const setSoundEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(SOUND_ENABLED_STORAGE_KEY, String(enabled));
};

const resumeAudioContextIfNeeded = async (context: AudioContext) => {
  if (context.state === 'suspended') {
    await context.resume();
  }
};

const createTone = (
  context: AudioContext,
  {
    frequency,
    duration,
    volume,
    type,
    delay = 0,
  }: {
    frequency: number;
    duration: number;
    volume: number;
    type: OscillatorType;
    delay?: number;
  }
) => {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  const startTime = context.currentTime + delay;
  const attack = 0.01;
  const release = Math.max(duration - attack, 0.04);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);

  gainNode.gain.setValueAtTime(0.0001, startTime);
  gainNode.gain.exponentialRampToValueAtTime(volume, startTime + attack);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + release);

  oscillator.connect(gainNode);
  gainNode.connect(context.destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
};

export const playSound = async (soundName: SoundName): Promise<void> => {
  if (!isSoundEnabled()) {
    return;
  }

  const context = getAudioContext();
  if (!context) {
    return;
  }

  try {
    await resumeAudioContextIfNeeded(context);

    // Comentario em pt-BR: os sons sao sintetizados no navegador para evitar peso com arquivos de audio.
    switch (soundName) {
      case 'tap':
        createTone(context, { frequency: 540, duration: 0.09, volume: 0.03, type: 'triangle' });
        break;
      case 'toggle-on':
        createTone(context, { frequency: 620, duration: 0.1, volume: 0.035, type: 'triangle' });
        createTone(context, { frequency: 820, duration: 0.12, volume: 0.025, type: 'sine', delay: 0.04 });
        break;
      case 'toggle-off':
        createTone(context, { frequency: 430, duration: 0.1, volume: 0.03, type: 'triangle' });
        createTone(context, { frequency: 320, duration: 0.12, volume: 0.02, type: 'sine', delay: 0.04 });
        break;
      case 'success':
        createTone(context, { frequency: 520, duration: 0.11, volume: 0.03, type: 'triangle' });
        createTone(context, { frequency: 680, duration: 0.13, volume: 0.028, type: 'triangle', delay: 0.05 });
        createTone(context, { frequency: 880, duration: 0.16, volume: 0.022, type: 'sine', delay: 0.1 });
        break;
      case 'achievement':
        createTone(context, { frequency: 520, duration: 0.1, volume: 0.028, type: 'triangle' });
        createTone(context, { frequency: 660, duration: 0.12, volume: 0.026, type: 'triangle', delay: 0.05 });
        createTone(context, { frequency: 880, duration: 0.14, volume: 0.024, type: 'sine', delay: 0.1 });
        createTone(context, { frequency: 1180, duration: 0.18, volume: 0.018, type: 'sine', delay: 0.16 });
        break;
      case 'warning':
        createTone(context, { frequency: 300, duration: 0.12, volume: 0.03, type: 'sawtooth' });
        createTone(context, { frequency: 260, duration: 0.16, volume: 0.02, type: 'triangle', delay: 0.06 });
        break;
      default:
        break;
    }
  } catch (error) {
    console.warn('Nao foi possivel tocar o efeito sonoro.', error);
  }
};
